import { DestroyRef, inject } from '@angular/core';
import { EMPTY, Observable, catchError, distinctUntilChanged, from, switchMap, timer } from 'rxjs';

/**
 * Listado con recarga opcional.
 *
 * Esto es **todo** lo que hacía `Lista2Service` en IRIX: 3.373 líneas y más de
 * 60 servicios HTTP inyectados para repetir este mismo bloque de seis líneas por
 * cada entidad de la plataforma. Importarlo para leer `luminarias()` arrastraba
 * el grafo completo de la aplicación — colectivos, emergencias, cámaras.
 *
 * Acá es una función, y cada dominio la usa sobre su propio servicio HTTP
 * (ADR-005).
 *
 * @param fn        qué pedir. Se vuelve a llamar en cada tick.
 * @param segundos  cadencia. Con `<= 0` pide una sola vez y termina.
 * @param igual     cómo decidir que no cambió nada. Por defecto compara el JSON.
 */
export function polleado<T>(
  fn: () => Promise<T>,
  segundos = 0,
  igual: (a: T, b: T) => boolean = (a, b) => JSON.stringify(a) === JSON.stringify(b),
): Observable<T> {
  if (segundos <= 0) return from(fn());

  return timer(0, segundos * 1000).pipe(
    // Un tick que falla se saltea: sin el `catchError` adentro, el primer error
    // terminaba el observable y el sondeo no volvía a correr nunca más.
    switchMap(() => from(fn()).pipe(catchError(() => EMPTY))),
    // Sin esto, cada tick repinta la tabla entera aunque el backend haya
    // devuelto exactamente lo mismo.
    distinctUntilChanged(igual),
  );
}

/** Lo único que `recargarCada` necesita de un `resource()`. */
export interface Recargable {
  reload(): boolean;
}

/**
 * Refresco de fondo de uno o más `resource()`: cada `segundos` les llama a
 * `reload()`, sólo con la pestaña visible y cuando `activo()` lo permite.
 *
 * **Es la forma de sondear un `resource()`.** Un `tick` en los `params`
 * parece equivalente y no lo es: cada `params` nuevo es otra request, y
 * `resource()` descarta el valor anterior —`value()` vuelve a `undefined` y
 * `hasValue()` a `false`— hasta que llega la respuesta. En cada vuelta la tabla
 * pasaba al esqueleto de "cargando" (y perdía el scroll), el detalle desmontaba
 * el cuerpo entero con su mapa, y el mapa de luminarias vaciaba la fuente y la
 * volvía a llenar: el destello que se veía cada minuto. Con `reload()` la
 * request es la misma, el valor se conserva mientras llega el nuevo (estado
 * `reloading`) y el cambio, si lo hay, se aplica de una sola vez. Es lo que
 * hace IRIX con `Lista2Service`: la suscripción conserva lo último hasta que
 * emite otra cosa.
 *
 * En la vista: `isLoading() && !hasValue()` es la carga inicial (esqueleto);
 * `isLoading()` a secas es un refresco, que a lo sumo lleva un indicador
 * discreto, nunca uno que tape.
 *
 * Se llama en un contexto de inyección (constructor o inicializador de campo,
 * **después** de declarar los recursos): el intervalo se corta solo al
 * destruirse el componente o el servicio.
 */
export function recargarCada(
  recursos: readonly Recargable[],
  segundos: number,
  activo: () => boolean = () => true,
): void {
  const intervalo = setInterval(() => {
    if (document.visibilityState !== 'visible' || !activo()) return;
    // `reload()` no hace nada (devuelve `false`) si el recurso está en su
    // primera carga o inactivo: no se encima con una request en vuelo.
    for (const r of recursos) r.reload();
  }, segundos * 1000);
  inject(DestroyRef).onDestroy(() => clearInterval(intervalo));
}

/**
 * Igualdad por contenido, para el `equal` de un `resource()` sondeado: si la
 * respuesta es la misma, el valor no cambia y nada de lo que depende de él se
 * recalcula ni se repinta. Es el `distinctUntilChanged` de `polleado`.
 */
export function igualJson<T>(a: T, b: T): boolean {
  return a === b || JSON.stringify(a) === JSON.stringify(b);
}

/** Cadencias. Son las de IRIX, para no cambiar la carga sobre el backend. */
export const CADENCIA = {
  /** Listados y mapa. */
  listado: 60,
  /** Detalle de una entidad. */
  detalle: 30,
  /** Eventos en vivo, sin WebSocket. `INTERVALO_POLLING_SIN_WS` en IRIX. */
  eventosVivo: 30,
  /** Panorama: números agregados, no hace falta más seguido. */
  panorama: 120,
  /** Métricas de red. */
  metricas: 60,
} as const;
